import type { ElementType, FC, ReactNode } from 'react'

import { useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap } from '@/lib/gsap'
import { ease as refEase, spring } from '@/lib/motion'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

interface RevealProps {
    children: ReactNode
    as?: ElementType
    className?: string
    delay?: number
    /** Travel distance. Negative drops the block in from above. */
    y?: number
    duration?: number
    /** Stagger direct children instead of moving the block as one. */
    stagger?: number
    start?: string
    /** Extra scale held behind the entrance, as the reference does on cards. */
    scale?: number
    /** Use the drop curve instead of the settle curve, for copy from above. */
    drop?: boolean
}

/**
 * General-purpose block reveal for paragraphs, buttons and metadata rows.
 *
 * The defaults are the reference's own small-copy spring converted to a tween:
 * 60px of travel over 0.9s. Deliberately quieter than the heading and image
 * gestures so it supports them rather than competing with them.
 */
export const Reveal: FC<RevealProps> = ({
    children,
    as: Tag = 'div',
    className,
    delay = 0,
    y = 60,
    duration = spring.copy.duration,
    stagger,
    start = 'top 88%',
    scale,
    drop = false
}): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        const context = gsap.context(() => {
            gsap.set(element, { visibility: 'visible' })

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return
            }

            const targets = stagger ? Array.from(element.children) : element

            gsap.fromTo(
                targets,
                { y, opacity: 0, scale: scale ?? 1 },
                {
                    y: 0,
                    opacity: 1,
                    scale: 1,
                    duration,
                    delay,
                    ease: drop ? refEase.drop : spring.copy.ease,
                    stagger: stagger ?? 0,
                    scrollTrigger: {
                        trigger: element,
                        start,
                        once: true
                    }
                }
            )
        }, element)

        return () => context.revert()
    }, [delay, y, duration, stagger, start, scale, drop])

    return (
        <Tag ref={root} data-reveal className={cn(className)}>
            {children}
        </Tag>
    )
}
